import { html } from 'htm/preact';
import { useEffect, useRef } from 'preact/hooks';

const DELAY_OPTIONS = [
  { label: '3m',  ms: 3  * 60 * 1000 },
  { label: '15m', ms: 15 * 60 * 1000 },
  { label: '30m', ms: 30 * 60 * 1000 },
  { label: '1h',  ms: 60 * 60 * 1000 },
  { label: '2h',  ms: 2  * 60 * 60 * 1000 },
];

const KEEPALIVE_OPTIONS = [
  { label: '30m', ms: 30 * 60 * 1000 },
  { label: '1h',  ms: 60 * 60 * 1000 },
  { label: '2h',  ms: 2  * 60 * 60 * 1000 },
  { label: '3h',  ms: 3  * 60 * 60 * 1000 },
  { label: '6h',  ms: 6  * 60 * 60 * 1000 },
];

const SESSION_WINDOW_MS = 24 * 60 * 60 * 1000;

const chipBase = 'padding:7px 14px;border-radius:10px;font-size:0.82rem;font-weight:600;cursor:pointer;font-family:inherit;-webkit-tap-highlight-color:transparent;transition:all 0.15s ease;';
const delayChipStyle = chipBase + 'background:rgba(37,211,102,0.1);border:1px solid rgba(37,211,102,0.3);color:#25D366;';
const keepAliveChipStyle = chipBase + 'background:rgba(96,165,250,0.1);border:1px solid rgba(96,165,250,0.3);color:#93c5fd;';

export function SchedulePopover({ phone, channelPhoneId, lastInboundTs, onClose, onScheduled }) {
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    setTimeout(() => document.addEventListener('pointerdown', handler), 50);
    return () => document.removeEventListener('pointerdown', handler);
  }, [onClose]);

  const remainingMs = lastInboundTs
    ? Math.max(0, (lastInboundTs * 1000 + SESSION_WINDOW_MS) - Date.now())
    : null;

  const remainingLabel = remainingMs != null
    ? remainingMs <= 0 ? 'expired'
      : remainingMs < 60 * 60 * 1000 ? `${Math.ceil(remainingMs / 60000)}min`
      : `${(remainingMs / 3600000).toFixed(1)}h`
    : null;

  const handleSchedule = (scheduleType, delayMs, triggerOffsetMs) => {
    const payload = {
      messaging_product: 'whatsapp',
      recipient_type: 'individual',
      to: phone,
      type: 'text',
      text: { body: '' },
      _phone_number_id: channelPhoneId,
    };
    onClose();
    if (onScheduled) {
      onScheduled(scheduleType, delayMs, triggerOffsetMs, payload);
    }
  };

  return html`
    <div
      ref=${ref}
      style="position:absolute;bottom:100%;right:0;margin-bottom:8px;width:min(92vw,300px);background:#151925;border:1px solid rgba(255,255,255,0.12);border-radius:16px;padding:16px;box-shadow:0 -8px 32px rgba(0,0,0,0.5);z-index:200;animation:schedPopIn 0.15s ease-out"
    >
      <div style="font-size:0.72rem;font-weight:700;color:#7d8799;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:12px;display:flex;align-items:center;gap:6px">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
        </svg>
        Schedule send
      </div>

      <div style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:14px">
        ${DELAY_OPTIONS.map(opt => {
          const tight = remainingMs != null && opt.ms > remainingMs;
          return html`
            <button
              key=${'d_' + opt.label}
              style=${tight
                ? chipBase + 'background:rgba(245,158,11,0.1);border:1px solid rgba(245,158,11,0.4);color:#fbbf24;'
                : delayChipStyle}
              onClick=${() => handleSchedule('delay', opt.ms, null)}
            >${opt.label}</button>
          `;
        })}
      </div>

      <div style="display:flex;align-items:center;gap:8px;margin-bottom:12px">
        <div style="flex:1;height:1px;background:rgba(255,255,255,0.08)"></div>
        <span style="font-size:0.66rem;color:#7d8799;white-space:nowrap">before session expires</span>
        <div style="flex:1;height:1px;background:rgba(255,255,255,0.08)"></div>
      </div>

      <div style="display:flex;flex-wrap:wrap;gap:8px">
        ${KEEPALIVE_OPTIONS.map(opt => {
          // T5 — a keep-alive chip is infeasible when the offset exceeds
          // the remaining session window (cron would immediately fail
          // the row with 'session_expired' on the next tick). Disable
          // visually + functionally instead of letting the user create
          // a doomed row. aria-disabled + title tooltip for a11y.
          const infeasible = remainingMs != null && opt.ms >= remainingMs;
          const disabledStyle = chipBase + 'background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.08);color:rgba(255,255,255,0.3);cursor:not-allowed;';
          return html`
            <button
              key=${'k_' + opt.label}
              aria-disabled=${infeasible ? 'true' : 'false'}
              disabled=${infeasible}
              title=${infeasible ? 'Not enough session time remaining for this offset' : ''}
              style=${infeasible ? disabledStyle : keepAliveChipStyle}
              onClick=${() => { if (!infeasible) handleSchedule('keep_alive', null, opt.ms); }}
            >${opt.label}</button>
          `;
        })}
      </div>
      <div style="font-size:0.66rem;color:#7d8799;margin-top:8px;display:flex;align-items:center;gap:4px">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
        </svg>
        Adjusts if they reply
      </div>

      ${remainingLabel && html`
        <div style="margin-top:12px;padding-top:10px;border-top:1px solid rgba(255,255,255,0.08);font-size:0.72rem;color:${remainingMs < 3600000 ? '#fbbf24' : '#7d8799'}">
          Session closes in ${remainingLabel}
        </div>
      `}
    </div>
  `;
}
